import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, buttonVariants } from '@resource-kit/ui/react';
import { cn } from '@resource-kit/ui';

interface OverviewCardProps {
  title: string;
  subtitle?: ReactNode;
  /** Extra header content (a count badge). */
  aside?: ReactNode;
  /** Right-aligned header content, before the link (a status badge). */
  end?: ReactNode;
  /** Header link to the page that acts on this card's data. */
  link?: { to: string; label: string };
  className?: string;
  children: ReactNode;
}

/** The shared frame of every overview card: title, subtitle, header link. */
export function OverviewCard({
  title,
  subtitle,
  aside,
  end,
  link,
  className,
  children,
}: OverviewCardProps) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row flex-wrap items-center gap-2 space-y-0 pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {subtitle ? <span className="text-xs text-muted-foreground">{subtitle}</span> : null}
        {aside}
        <span data-testid="overview-card-spacer" className="flex-1" />
        {end}
        {link ? (
          <Link to={link.to} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            {link.label}
          </Link>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-2 text-sm">{children}</CardContent>
    </Card>
  );
}

/** Loading / failed placeholder line shared by the cards. */
export function CardStatus({ loading, label }: { loading: boolean; label: string }) {
  return (
    <p className={cn('text-sm', loading ? 'text-muted-foreground' : 'text-destructive')}>{label}</p>
  );
}
